<script lang="ts">
  import { get } from 'svelte/store';
  import { screens, playerTruth, glossary } from '../store';
  import { setupEnvironment } from './models/environment.type';
  import { createEnemyInstance, type EnemyDefinition } from './models/enemy.type';
  import { clonePlayer, type Player } from './models/player.type';
  import { createDie, type Die as DieType } from './models/die.type';
  import Portrait from './components/portrait.svelte';
  import Die from './dice-tray/die.svelte';
  import { randomInt } from './utils/math';
  import { actionHistory } from '../store';
  import { track } from './utils/playerTracking';
  import Frame from './components/frame.svelte';
  import Chrome from './components/chrome.svelte';
  import { EnvironmentType } from './models/environment.type';
  import { Screen } from './models/screen.type';

  interface Props {
    data: any;
  }

  let { data }: Props = $props();

  const definition: EnemyDefinition = data.battle.enemy;

  const player = clonePlayer($playerTruth);
  player.statusEffects.sort((a, b) => {
    return b.priority - a.priority;
  });

  const enemy: Player = createEnemyInstance(data.battle.level, definition);

  // Chosen once so the backdrop shows the same place the battle is set in.
  const environment: EnvironmentType = data.battle.environment
    ? data.battle.environment
    : definition.environments && definition.environments.length
      ? definition.environments[randomInt(0, definition.environments.length - 1)]
      : EnvironmentType.Forest;
  setupEnvironment(environment);

  // Most enemies roll matched pairs, so show each die once with a count.
  const diceGroups: Array<{ die: DieType; count: number }> = [];
  (enemy.dice ?? []).forEach((die, index) => {
    const existing = diceGroups.find((group) => group.die.type === die.type);
    if (existing) {
      existing.count++;
      return;
    }
    diceGroups.push({ die: createDie(die.type, index, `start-${die.type}`), count: 1 });
  });

  let root: HTMLElement;
  let started = false;

  function start() {
    if (started) return;
    started = true;

    //create player instances
    screens.set(Screen.Battle, {
      battle: data.battle,
      player: player,
      enemy: enemy,
    });

    actionHistory.length = 0;
  }

  function viewManual() {
    glossary.open.set(true);
    glossary.selected.set('enemies');
    glossary.searchTerm.set(enemy.title);
  }

  function keydown(event: KeyboardEvent) {
    if (event.key !== 'Enter') return;
    // Only while this screen is the one on show, and not under the manual.
    if (!root?.closest('.full-screen')?.classList.contains('visible')) return;
    if (get(glossary.open)) return;
    start();
  }

  track('Battle Start', {
    player_class: $playerTruth.type,
    player_archetype: $playerTruth.archetype,
    player_level: $playerTruth.level,

    enemy_class: data.battle.enemy.type,
    enemy_level: data.battle.enemy.level,
  });
</script>

<svelte:window onkeydown={keydown} />

<div class="background {environment}"></div>

<div class="stage" bind:this={root}>
  <Frame>
    <p class="step-title">Prepare to Fight</p>

    <div class="matchup">
      <div class="vs">
        <div class="side">
          <Portrait data={player} />
          <h2>{player.title}</h2>
          <span class="stat">Level {player.level + 1}</span>
          <span class="stat">{player.healthCurrent}/{player.healthMax} HP</span>
        </div>

        <span class="versus">VS</span>

        <div class="side">
          <Portrait data={enemy} />
          <h2>{enemy.title}</h2>
          <span class="stat">Level {enemy.level + 1}</span>
          <span class="stat">{enemy.healthMax} HP</span>
        </div>
      </div>

      <div class="details">
        <div class="dossier">
          {#if enemy.text}
            <p class="flavour">{enemy.text}</p>
          {/if}

          {#if diceGroups.length}
            <div class="loadout" aria-label="Enemy dice">
              {#each diceGroups as group (group.die.id)}
                <div class="die-container">
                  <Die die={group.die} die3D={false} hover={true} />
                  {#if group.count > 1}
                    <span class="count">&times;{group.count}</span>
                  {/if}
                </div>
              {/each}
            </div>
          {/if}

          {#if definition.strengths?.length || definition.weaknesses?.length}
            <div class="tactics">
              {#if definition.strengths?.length}
                <div class="column strengths">
                  <span class="column-label">Strengths</span>
                  <ul>
                    {#each definition.strengths as strength}
                      <li>{strength}</li>
                    {/each}
                  </ul>
                </div>
              {/if}
              {#if definition.weaknesses?.length}
                <div class="column weaknesses">
                  <span class="column-label">Weaknesses</span>
                  <ul>
                    {#each definition.weaknesses as weakness}
                      <li>{weakness}</li>
                    {/each}
                  </ul>
                </div>
              {/if}
            </div>
          {/if}
        </div>

        <div class="button-container">
          <Chrome click={viewManual} edge="circle" chroming="obsidian" sound={true}>
            <div class="button">Enemy Info</div>
          </Chrome>
          <Chrome click={start} edge="circle" chroming="gold">
            <div class="button">Fight</div>
          </Chrome>
        </div>
      </div>
    </div>
  </Frame>
</div>

<style lang="less">
  @import '../defines.less';

  // Landscape phones: too short to stack the face-off over the details.
  @short: ~'(orientation: landscape) and (max-height: 560px)';

  :root {
    background-color: var(--background-colour);
  }

  .background {
    height: 100vh;
    width: 100vw;
    position: absolute;
    top: 0;
    left: 0;
    filter: blur(5px);
    z-index: 0;
    box-shadow: inset 0 0 40px 10px rgba(0, 0, 0, 1);
  }

  .stage {
    // Sized from the viewport, like player select, rather than the app-wide
    // breakpoints -- two portraits and a VS have to fit across a phone.
    --column-size: clamp(90px, min(28vw, 26vh), 240px);
    --dice-size: clamp(34px, 5.5vh, 56px);
    // The face-off's spacing, named so the dossier can find the enemy's
    // portrait from them.
    --vs-gap: clamp(8px, 2vw, 24px);
    --versus-width: calc(var(--column-size) * 0.45);

    position: absolute;
    inset: 0;
    display: grid;
    box-sizing: border-box;
    padding: 12px;
    // A last resort for a screen too short for the frame: scroll rather than
    // leave Fight out of reach. The frame's auto margins centre it.
    overflow-y: auto;
    text-align: center;
    text-shadow: 2px 2px black;
  }

  .step-title {
    margin: 0 0 clamp(8px, 2vh, 20px);
    font-size: clamp(0.95em, min(3.6vw, 2.4vh), 1.3em);
    letter-spacing: 0.18em;
    text-transform: uppercase;
    opacity: 0.7;
  }

  // Not .layout -- game-board.less styles that class globally, absolutely
  // positioned and clipped.
  .matchup {
    display: grid;
    grid-gap: clamp(10px, 2.5vh, 28px);

    @media @short {
      grid-template-columns: auto 1fr;
      // Top-aligned, so the dossier's notch can meet the portrait's middle.
      align-items: start;
    }
  }

  .vs {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    grid-gap: var(--vs-gap);

    .side {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: var(--column-size);
      grid-gap: 4px;
    }

    h2 {
      margin-top: clamp(4px, 1vh, 10px);
      font-size: clamp(1.1em, min(4.5vw, 3.6vh), 1.8em);
      line-height: 1.1;
    }

    .stat {
      font-size: clamp(12px, 1.7vh, 15px);
      letter-spacing: 0.08em;
      text-transform: uppercase;
      opacity: 0.7;
    }

    .versus {
      align-self: center;
      width: var(--versus-width);
      text-align: center;
      margin-top: calc(var(--column-size) / -3);
      font-size: clamp(1.4em, min(6vw, 5vh), 2.6em);
      font-weight: 700;
      font-style: italic;
      color: @edge-shine;
    }
  }

  .details {
    display: flex;
    flex-direction: column;
    align-items: center;
    grid-gap: clamp(10px, 2vh, 20px);

    @media @short {
      align-items: stretch;
      text-align: left;
    }
  }

  // Everything below the portraits is about the enemy, so it sits in one
  // panel that points up at the enemy's portrait and is edged in the same
  // colour as that portrait's ring.
  .dossier {
    // From the face-off's centre to the centre of the enemy's portrait. The
    // panel is centred under the face-off, so this is where its notch goes.
    --enemy-centre: calc(var(--versus-width) / 2 + var(--vs-gap) + var(--column-size) / 2);
    --accent: @enemy-tone;

    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    grid-gap: clamp(10px, 2vh, 18px);
    width: 100%;
    max-width: calc(55ch + 40px);
    margin: 4px auto 0;
    box-sizing: border-box;
    padding: clamp(10px, 2vh, 18px) clamp(12px, 2vw, 20px);
    background: @panel-sunken;
    border: 1px solid var(--accent);
    border-radius: 6px;
    box-shadow:
      inset 0 0 14px fade(@black, 70%),
      0 0 6px fade(@black, 60%);

    &::before {
      content: '';
      position: absolute;
      top: -8px;
      left: calc(50% + var(--enemy-centre));
      width: 14px;
      height: 14px;
      margin-left: -8px;
      background: @panel-sunken;
      border-top: 1px solid var(--accent);
      border-left: 1px solid var(--accent);
      transform: rotate(45deg);
    }

    @media @short {
      align-items: stretch;
      grid-gap: 6px;
      margin: 0;
      padding: 8px 12px;

      // Beside the face-off now, so point left at it instead.
      &::before {
        top: calc(var(--column-size) / 2 - 8px);
        left: -8px;
        margin-left: 0;
        transform: rotate(-45deg);
      }
    }
  }

  .flavour {
    max-width: 55ch;
    margin: 0 auto;
    font-size: clamp(14px, 1.9vh, 17px);
    line-height: 1.45;

    @media @short {
      margin: 0;
      font-size: 13px;
      line-height: 1.35;
    }
  }

  .loadout {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    grid-gap: 12px;

    @media @short {
      justify-content: flex-start;
    }

    .die-container {
      position: relative;
    }

    // How many of that die the enemy rolls.
    .count {
      position: absolute;
      right: -6px;
      bottom: -6px;
      padding: 0 5px;
      font-size: 0.75rem;
      line-height: 1.5;
      text-shadow: none;
      background: var(--background-colour-dark);
      border: 1px solid var(--background-colour-light);
      border-radius: 999px;
      pointer-events: none;
    }
  }

  .tactics {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(130px, 100%), 1fr));
    grid-gap: 10px 16px;
    width: 100%;
    max-width: 55ch;
    text-align: left;

    .column-label {
      display: block;
      margin-bottom: 2px;
      font-size: 0.75rem;
      letter-spacing: 0.1em;
      text-transform: uppercase;
    }
    .strengths .column-label {
      color: @green;
    }
    .weaknesses .column-label {
      color: @red;
    }

    ul {
      margin: 0;
      padding-left: 18px;
      font-size: clamp(13px, 1.8vh, 15px);
    }
  }

  .button-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: end;
    width: 100%;
    grid-gap: 20px;
  }

  .button {
    white-space: nowrap;
    font-size: clamp(18px, min(4vh, 4.8vw), 28px);
    padding: 6px 12px;
    color: #ffffd5;
  }
</style>
